import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from '@/components/ui/use-toast';

export interface CollectionContact {
  id: string;
  user_id: string;
  customer_id?: string;
  name: string;
  email?: string;
  phone?: string;
  contact_type: 'primary' | 'billing' | 'technical' | 'legal';
  address?: any;
  preferences: any;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateCollectionContactData {
  customer_id?: string;
  name: string;
  email?: string;
  phone?: string;
  contact_type: 'primary' | 'billing' | 'technical' | 'legal';
  address?: any;
  preferences?: any;
}

export interface UpdateCollectionContactData extends CreateCollectionContactData {
  id: string;
  is_active?: boolean;
}

export const useCollectionContacts = (customerId?: string) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const contactsQuery = useQuery({
    queryKey: ['collection-contacts', customerId, user?.id],
    queryFn: async () => {
      let query = supabase
        .from('collection_contacts')
        .select('*')
        .order('created_at', { ascending: false });

      if (customerId) {
        query = query.eq('customer_id', customerId);
      }

      const { data, error } = await query;
      
      if (error) throw error;
      return data as CollectionContact[];
    },
    enabled: !!user?.id,
  });

  const createContactMutation = useMutation({
    mutationFn: async (contactData: CreateCollectionContactData) => {
      const { data, error } = await supabase
        .from('collection_contacts')
        .insert([{
          ...contactData,
          user_id: user?.id,
          preferences: contactData.preferences || {},
          is_active: true,
        }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collection-contacts'] });
      toast({
        title: "Contact created",
        description: "Collection contact has been created successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to create contact",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateContactMutation = useMutation({
    mutationFn: async ({ id, ...contactData }: UpdateCollectionContactData) => {
      const { data, error } = await supabase
        .from('collection_contacts')
        .update(contactData)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collection-contacts'] });
      toast({
        title: "Contact updated",
        description: "Collection contact has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to update contact",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteContactMutation = useMutation({
    mutationFn: async (contactId: string) => {
      const { error } = await supabase
        .from('collection_contacts')
        .delete()
        .eq('id', contactId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collection-contacts'] });
      toast({
        title: "Contact deleted",
        description: "Collection contact has been deleted successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to delete contact",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    contacts: contactsQuery.data || [],
    isLoading: contactsQuery.isLoading,
    error: contactsQuery.error,
    createContact: createContactMutation.mutate,
    updateContact: updateContactMutation.mutate,
    deleteContact: deleteContactMutation.mutate,
    isCreating: createContactMutation.isPending,
    isUpdating: updateContactMutation.isPending,
    isDeleting: deleteContactMutation.isPending,
  };
};